<script>
import { mapGetters } from 'vuex';
import { useAnnouncements } from 'dashboard/composables/useAnnouncements';
import { dynamicTime } from 'shared/helpers/timeHelper';

const NEWS_SEEN_KEY = 'system_notice_news_seen';
const NEWS_SEEN_LIMIT = 200;

const readStorage = key => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const parseSeenPairs = signature => {
  try {
    const parsed = JSON.parse(signature);
    return Array.isArray(parsed) ? parsed.filter(Array.isArray) : [];
  } catch {
    return [];
  }
};

const pairKey = ([id, updatedAt]) => `${id}:${updatedAt}`;

const parseSeen = signature => parseSeenPairs(signature).map(pairKey);

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

export default {
  name: 'AnnouncementsIndicator',
  setup() {
    const { announcements, ensureAccount, startListening, stopListening } =
      useAnnouncements();
    return { announcements, ensureAccount, startListening, stopListening };
  },
  data() {
    return {
      isOpen: false,
      panelTop: 0,
      newsSeenSignature: readStorage(NEWS_SEEN_KEY),
      seenBeforeOpen: [],
    };
  },
  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),
    newsAnnouncements() {
      return this.announcements.filter(a => a.kind !== 'incident');
    },
    hasNews() {
      return this.newsAnnouncements.length > 0;
    },
    hasUnseenNews() {
      const seen = parseSeen(this.newsSeenSignature);
      return this.newsAnnouncements.some(
        a => !seen.includes(`${a.id}:${a.updated_at}`)
      );
    },
  },
  watch: {
    hasNews(newValue) {
      if (!newValue) this.isOpen = false;
    },
    currentAccountId: {
      immediate: true,
      handler(accountId) {
        this.ensureAccount(accountId);
      },
    },
  },
  mounted() {
    this.startListening();
  },
  beforeDestroy() {
    this.stopListening();
  },
  methods: {
    togglePanel() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) return;

      this.alignPanelToButton();
      const previous = parseSeenPairs(this.newsSeenSignature);
      const current = this.newsAnnouncements.map(a => [a.id, a.updated_at]);
      const currentKeys = new Set(current.map(pairKey));
      const kept = previous.filter(pair => !currentKeys.has(pairKey(pair)));
      this.seenBeforeOpen = previous.map(pairKey);
      this.newsSeenSignature = JSON.stringify(
        [...kept, ...current].slice(-NEWS_SEEN_LIMIT)
      );
      writeStorage(NEWS_SEEN_KEY, this.newsSeenSignature);
    },
    alignPanelToButton() {
      const button = this.$refs.trigger;
      if (!button) return;

      const { top, height } = button.getBoundingClientRect();
      const panelHeight = 440;
      const margin = 16;
      const centered = top + height / 2 - panelHeight / 2;
      const maxTop = window.innerHeight - panelHeight - margin;
      this.panelTop = Math.max(margin, Math.min(centered, maxTop));
    },
    isNew(announcement) {
      const key = `${announcement.id}:${announcement.updated_at}`;
      return !this.seenBeforeOpen.includes(key);
    },
    closePanel() {
      this.isOpen = false;
    },
    updatedAgo(isoDate) {
      const parsedMs = new Date(isoDate).getTime();
      if (Number.isNaN(parsedMs)) return '';
      return dynamicTime(parsedMs / 1000);
    },
  },
};
</script>

<template>
  <div v-on-clickaway="closePanel" class="relative">
    <button
      v-if="hasNews"
      ref="trigger"
      data-notice="news"
      class="relative flex items-center justify-center w-10 h-10 p-0 my-2 rounded-lg text-slate-700 dark:text-slate-100 hover:bg-gray-gray-2 dark:hover:bg-dark-dark-4 hover:text-basic-primaryText dark:hover:text-basic-primaryText"
      :class="{ 'bg-woot-50 dark:bg-slate-800 !text-woot-500': isOpen }"
      :title="$t('STATUS_PAGE.ANNOUNCEMENTS_BUTTON_LABEL')"
      :aria-label="$t('STATUS_PAGE.ANNOUNCEMENTS_BUTTON_LABEL')"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="togglePanel"
    >
      <fluent-icon-v2 icon="gift" size="20" />
      <span
        v-if="hasUnseenNews"
        data-testid="news-unseen-dot"
        class="absolute w-2 h-2 rounded-full top-1.5 right-1.5 bg-woot-500 ring-2 ring-white dark:ring-slate-900"
      />
    </button>
    <dialog
      v-if="isOpen"
      open
      :aria-label="$t('STATUS_PAGE.ANNOUNCEMENTS_TITLE')"
      class="fixed inset-auto left-[4.5rem] w-96 m-0 p-0 text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden"
      :style="{ top: `${panelTop}px` }"
    >
      <div
        class="flex items-center gap-2 h-14 pl-5 pr-3 border-b border-slate-100 dark:border-slate-700"
      >
        <fluent-icon-v2
          icon="gift"
          size="18"
          class="text-woot-600 dark:text-woot-300"
        />
        <span
          class="text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          {{ $t('STATUS_PAGE.ANNOUNCEMENTS_TITLE') }}
        </span>
        <button
          data-close="panel"
          class="flex items-center justify-center w-7 h-7 p-0 ml-auto rounded-md bg-transparent border-0 text-slate-400 hover:text-slate-700 hover:bg-slate-50 dark:hover:text-slate-100 dark:hover:bg-slate-700"
          :aria-label="$t('STATUS_PAGE.CLOSE')"
          :title="$t('STATUS_PAGE.CLOSE')"
          @click="closePanel"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div
        class="flex flex-col max-h-96 overflow-y-auto [scrollbar-width:thin]"
      >
        <article
          v-for="announcement in newsAnnouncements"
          :key="`news-${announcement.id}`"
          data-news-item
          class="flex flex-col gap-1 px-5 py-4 border-b border-slate-50 dark:border-slate-700 last:border-b-0"
        >
          <span class="flex items-center gap-2 text-xs text-slate-400">
            <span
              v-if="isNew(announcement)"
              data-new-badge
              class="px-2 rounded-full bg-woot-50 text-woot-600 dark:bg-woot-800/40 dark:text-woot-200"
            >
              {{ $t('STATUS_PAGE.NEW_BADGE') }}
            </span>
            {{ updatedAgo(announcement.updated_at) }}
          </span>
          <p
            class="mb-0 text-[15px] font-medium text-slate-800 dark:text-slate-100"
          >
            {{ announcement.title }}
          </p>
          <p
            class="mb-0 text-[13px] leading-5 text-slate-600 dark:text-slate-300"
          >
            {{ announcement.message }}
          </p>
          <a
            v-if="announcement.link"
            :href="announcement.link"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[13px] font-medium text-woot-500 hover:underline"
          >
            {{ $t('STATUS_PAGE.ANNOUNCEMENT_LINK') }}
          </a>
        </article>
      </div>
    </dialog>
  </div>
</template>
